import { useState, useEffect } from 'react'
import { Header } from '../components/layout/Header'

interface PlatformMetric {
  id: string
  platform: string
  followers: number
  engagement_rate: number
  impressions: number
  clicks: number
  shares: number
  leads: number
  growth_rate: number
}

interface PostPerformance {
  id: string
  platform: string
  content: string
  impressions: number
  engagements: number
  clicks: number
  shares: number
  comments: number
  engagement_rate: number
}

interface Statistics {
  total_followers: number
  total_impressions: number
  avg_engagement_rate: number
  total_clicks: number
  total_leads: number
  follower_growth: number
}

export default function SocialAnalyticsPage() {
  const [platforms, setPlatforms] = useState<PlatformMetric[]>([])
  const [posts, setPosts] = useState<PostPerformance[]>([])
  const [statistics, setStatistics] = useState<Statistics | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    fetchData()
  }, [])

  const fetchData = async () => {
    try {
      const [platformsRes, postsRes, statsRes] = await Promise.all([
        fetch('/api/socialanalytics/platforms'),
        fetch('/api/socialanalytics/posts'),
        fetch('/api/socialanalytics/statistics')
      ])
      if (platformsRes.ok) setPlatforms(await platformsRes.json())
      if (postsRes.ok) setPosts(await postsRes.json())
      if (statsRes.ok) setStatistics(await statsRes.json())
    } catch (error) {
      console.error('Error fetching social analytics data:', error)
    } finally {
      setLoading(false)
    }
  }

  const formatNumber = (value: number) => {
    return new Intl.NumberFormat('en-US').format(value)
  }

  const getPlatformColor = (platform: string) => {
    const colors: { [key: string]: string } = {
      'LinkedIn': 'bg-blue-100 text-blue-800',
      'Twitter/X': 'bg-gray-100 text-gray-800',
      'Facebook': 'bg-indigo-100 text-indigo-800',
      'Instagram': 'bg-pink-100 text-pink-800'
    }
    return colors[platform] || 'bg-gray-100 text-gray-800'
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50">
        <Header />
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <Header />
      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Social Media Analytics</h1>
          <p className="mt-2 text-gray-600">Phase 245 - Social platform performance</p>
        </div>

        {statistics && (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-8">
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Followers</p>
              <p className="text-2xl font-bold text-gray-900">{formatNumber(statistics.total_followers)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Impressions</p>
              <p className="text-2xl font-bold text-blue-600">{formatNumber(statistics.total_impressions)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Engagement Rate</p>
              <p className="text-2xl font-bold text-purple-600">{statistics.avg_engagement_rate}%</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Total Clicks</p>
              <p className="text-2xl font-bold text-green-600">{formatNumber(statistics.total_clicks)}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Leads</p>
              <p className="text-2xl font-bold text-orange-600">{statistics.total_leads}</p>
            </div>
            <div className="bg-white rounded-lg shadow p-6">
              <p className="text-sm text-gray-500">Growth</p>
              <p className="text-2xl font-bold text-green-600">+{statistics.follower_growth}%</p>
            </div>
          </div>
        )}

        <div className="bg-white rounded-lg shadow mb-6">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Platform Performance</h2>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Platform</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Followers</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Engagement</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Impressions</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Clicks</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Leads</th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Growth</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {platforms.map((platform) => (
                  <tr key={platform.id}>
                    <td className="px-6 py-4">
                      <span className={`px-3 py-1 rounded-full text-sm font-medium ${getPlatformColor(platform.platform)}`}>{platform.platform}</span>
                    </td>
                    <td className="px-6 py-4 font-medium text-gray-900">{formatNumber(platform.followers)}</td>
                    <td className="px-6 py-4 text-purple-600">{platform.engagement_rate}%</td>
                    <td className="px-6 py-4 text-blue-600">{formatNumber(platform.impressions)}</td>
                    <td className="px-6 py-4 text-green-600">{formatNumber(platform.clicks)}</td>
                    <td className="px-6 py-4 text-orange-600">{platform.leads}</td>
                    <td className={`px-6 py-4 font-bold ${platform.growth_rate >= 0 ? 'text-green-600' : 'text-red-600'}`}>+{platform.growth_rate}%</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow">
          <div className="px-6 py-4 border-b">
            <h2 className="text-lg font-semibold">Top Posts</h2>
          </div>
          <div className="p-6 space-y-4">
            {posts.map((post) => (
              <div key={post.id} className="p-4 bg-gray-50 rounded-lg">
                <div className="flex items-center justify-between mb-2">
                  <div>
                    <span className={`px-2 py-1 text-xs rounded-full ${getPlatformColor(post.platform)}`}>{post.platform}</span>
                    <span className="ml-2 font-medium text-gray-900">{post.content}</span>
                  </div>
                  <span className="text-lg font-bold text-purple-600">{post.engagement_rate}%</span>
                </div>
                <div className="grid grid-cols-5 gap-2 text-sm">
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Impressions</p>
                    <p className="font-medium text-blue-600">{formatNumber(post.impressions)}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Engagements</p>
                    <p className="font-medium text-purple-600">{formatNumber(post.engagements)}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Clicks</p>
                    <p className="font-medium text-green-600">{formatNumber(post.clicks)}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Shares</p>
                    <p className="font-medium text-orange-600">{formatNumber(post.shares)}</p>
                  </div>
                  <div className="text-center p-2 bg-white rounded">
                    <p className="text-gray-500">Comments</p>
                    <p className="font-medium text-pink-600">{formatNumber(post.comments)}</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  )
}
